/** @jsxRuntime classic */
/** @jsx jsx */
import {
  FileWordOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
} from "@ant-design/icons";
import {
  AdminSheetButton,
  AuthRestrict,
  DEFAULT_PLAYER_SETTINGS,
  getPlaceholderRhymequizState,
  LyricsDocButton,
  LyricsPreview,
  ModeTag,
  mq,
  Player,
  RetakePlayer,
  ReviewMode,
  RhymequizBanner,
  RhymequizRecommendations,
  RhymequizState,
  SharedLayout,
  SharedLayoutChildProps,
  useRhymequizSlug,
} from "@app/components";
import {
  GradingSummary,
  RetakeSummary,
  SimpleGradingReport,
} from "@app/components/dist/grading-report";
import { colors } from "@app/components/src/__common__/theme";
import { PlayerSettings } from "@app/components/src/player/interfaces";
import {
  Rhymequizz,
  UserAccountType,
  useRhymequizBySlugQuery,
  useSharedQuery,
} from "@app/graphql";
import { EVENT_TYPE, track } from "@app/lib";
import { css, jsx } from "@emotion/react";
import { Button, Col, Modal, Row, Switch } from "antd";
import { capitalize } from "lodash";
import { NextPage } from "next";
import { useRouter } from "next/router";
import React, { useEffect, useMemo, useState } from "react";

import { LibraryStyleArtifact } from "../../library";

const RhymequizPage: NextPage = () => {
  const router = useRouter();
  const query = useSharedQuery();
  const slug = useRhymequizSlug();
  const { loading, error, data } = useRhymequizBySlugQuery({
    variables: { slug, userId: query.data?.currentUser?.id },
    fetchPolicy: "no-cache",
    skip: query.data?.currentUser?.id === undefined,
  });
  // Admin panel is putting all strings in a single element of array
  const vocabWords = data?.rhymequizzBySlug?.vocabWords;
  const fixedVocab = useMemo(() => {
    let fixed =
      vocabWords?.length !== 1 ? vocabWords : vocabWords[0]?.split(",");
    return fixed?.map((vocab) => capitalize(vocab as string));
  }, [vocabWords]);

  const [playerSettings, setPlayerSettings] = useState<PlayerSettings>(
    DEFAULT_PLAYER_SETTINGS
  );
  const [isShowExtra, setShowExtra] = useState<boolean>(true);
  const [isShowToggle, setShowToggle] = useState<boolean>(false);

  const [isFinished, setFinished] = useState<boolean>(false);
  const [modeState, setModeState] = useState<
    | "player"
    | "summary"
    | "review"
    | "retake-wrong"
    | "retake-full"
    | "retake-summary"
  >("player");
  const [rhymequizState, setRhymequizState] = useState<RhymequizState>(
    getPlaceholderRhymequizState()
  );
  const [
    retakeRhymequizState,
    setRetakeRhymequizState,
  ] = useState<RhymequizState | null>(null);
  const [isVocabModalVisible, setVocabModalVisible] = useState<boolean>(false);
  const [isLyricsPreviewVisible, setLyricsPreviewVisible] = useState<boolean>(
    false
  );
  const [width, setWidth] = useState<number>(800);

  const resizeWindow = () => {
    setWidth(window.innerWidth);
  };

  useEffect(() => {
    resizeWindow();
    window.addEventListener("resize", resizeWindow);
    return () => window.removeEventListener("resize", resizeWindow);
  }, []);

  const onQuizFinished = (
    rzState: RhymequizState,
    settings: PlayerSettings
  ) => {
    setModeState("summary");
    setPlayerSettings({
      ...settings,
      isAnswerPrefilled: false,
      isPauseForAnswers: true,
    });
    setRhymequizState(rzState);
  };

  const onPlayClicked = () => {
    setShowExtra(false);
    setShowToggle(true);
  };

  const onQuizRetakeFinished = (rzState: RhymequizState) => {
    setRetakeRhymequizState(rzState);
    setModeState("retake-summary");
    setTimeout(() => {
      window.scrollTo({ top: 290, left: 0, behavior: "smooth" });
    }, 500);
  };

  const onSeeReport = () => {
    window.scrollTo({ top: 60, left: 0, behavior: "smooth" });
    setFinished(true);
  };

  const quizName = `${data?.rhymequizzBySlug?.name ?? slug}`;
  const categorySlug =
    data?.rhymequizzBySlug?.topic?.subject?.category?.slug ?? "Category";
  const categoryName =
    data?.rhymequizzBySlug?.topic?.subject?.category?.name ?? "Category";
  const subjectId = data?.rhymequizzBySlug?.topic?.subject?.id;
  const subjectSlug = data?.rhymequizzBySlug?.topic?.subject?.slug ?? "Subject";
  const subjectName = data?.rhymequizzBySlug?.topic?.subject?.name ?? "Subject";
  const topicSlug = data?.rhymequizzBySlug?.topic?.slug ?? "Topic";
  const topicName = data?.rhymequizzBySlug?.topic?.name ?? "Topic";

  const assignmentsPreview =
    data?.rhymequizzBySlug?.rhymequizAssignmentsByRhymequizId;

  const renderedVocabWords = (
    <Row>
      {width <= 768 && quizName}
      <Row
        css={{
          paddingTop: "15px",
          paddingBottom: "15px",
          borderTop: `1px solid ${colors.lightGrey}`,
        }}
      >
        {fixedVocab?.map((vocab, idx) => (
          <Col
            xs={12}
            sm={12}
            md={8}
            lg={8}
            key={`${vocab}-${idx}`}
            css={s.word}
          >
            {vocab}
          </Col>
        ))}
      </Row>
    </Row>
  );

  const [playerScale, setPlayerScale] = useState<number>(1);
  const onFullscreenClicked = () => {
    const playerRef = document.getElementById("playerRef");
    if (playerRef) {
      playerRef.requestFullscreen();
      setTimeout(() => {
        setPlayerScale((window.innerWidth / 980) * 0.95);
      }, 1000);
    }
  };

  const [isFullScreen, setIsFullScreen] = useState(false);

  useEffect(() => {
    // Check if the browser supports the Fullscreen API
    if (document.fullscreenEnabled) {
      document.addEventListener("fullscreenchange", handleFullScreenChange);
    }

    return () => {
      document.removeEventListener("fullscreenchange", handleFullScreenChange);
    };
  }, []);

  const handleFullScreenChange = () => {
    setIsFullScreen(document.fullscreenElement !== null);
  };

  const ExitFullScreenButton = () => {
    return (
      <Button
        icon={<FullscreenExitOutlined />}
        css={{
          color: colors.darkGrey,
          position: "absolute",
          right: "8%",
          marginTop: 12,
        }}
        onClick={() => {
          document.exitFullscreen();
          setPlayerScale(1);
        }}
      ></Button>
    );
  };

  return (
    <div>
      {!isFinished && <LibraryStyleArtifact />}
      {!isFinished && modeState === "review" && (
        <ModeTag mode="review" top={350} />
      )}
      {!isFinished &&
        (modeState === "retake-wrong" || modeState === "retake-full") && (
          <ModeTag mode="retake" top={375} />
        )}
      <SharedLayout
        title={quizName}
        query={query}
        forbidWhen={AuthRestrict.LOGGED_OUT}
        titleHref={`/rhymequiz/[slug]`}
        titleHrefAs={`/rhymequiz/${slug}`}
      >
        {({ currentUser }: SharedLayoutChildProps) => {
          return !isFinished ? (
            <div>
              <RhymequizBanner
                quizName={quizName}
                category={{ name: categoryName, slug: categorySlug }}
                subject={{ name: subjectName, slug: subjectSlug }}
                topic={{ name: topicName, slug: topicSlug }}
                standardsCode={data?.rhymequizzBySlug?.standardsCode}
              ></RhymequizBanner>

              {modeState === "player" && (
                <div
                  id="playerRef"
                  css={{ "&:fullscreen": { backgroundColor: "white" } }}
                >
                  <div
                    id="fullScreenWrapper"
                    css={isFullScreen ? s.fullScreenWrapper(playerScale) : {}}
                  >
                    <Player
                      error={error}
                      loading={loading}
                      rhymequiz={data?.rhymequizzBySlug as Rhymequizz}
                      isFullScreen={isFullScreen}
                      onQuizFinished={onQuizFinished}
                      onPlayClicked={onPlayClicked}
                    />

                    {isFullScreen && <ExitFullScreenButton />}
                  </div>
                </div>
              )}

              {modeState === "summary" && (
                <GradingSummary
                  coupletState={rhymequizState.coupletState}
                  onReview={() => setModeState("review")}
                  onSkipToReport={onSeeReport}
                />
              )}

              {modeState === "review" && (
                <div
                  css={{
                    marginTop: 50,
                    marginBottom: 50,
                    [mq.md]: { marginBottom: 0 },
                  }}
                >
                  <ReviewMode
                    coupletState={rhymequizState.coupletState}
                    onRetakeFull={() => {
                      window.scrollTo({
                        top: 300,
                        left: 0,
                        behavior: "smooth",
                      });
                      setModeState("retake-full");
                    }}
                    onRetakeWrongOnly={() => {
                      window.scrollTo({
                        top: 300,
                        left: 0,
                        behavior: "smooth",
                      });
                      setModeState("retake-wrong");
                    }}
                    onSkipToReport={onSeeReport}
                  />
                </div>
              )}

              {!isFinished &&
                (modeState === "retake-wrong" ||
                  modeState === "retake-full") && (
                  <div
                    id="playerRef"
                    css={{ "&:fullscreen": { backgroundColor: "white" } }}
                  >
                    <div
                      id="fullScreenWrapper"
                      css={isFullScreen ? s.fullScreenWrapper(playerScale) : {}}
                    >
                      <RetakePlayer
                        existingState={rhymequizState}
                        mode={modeState}
                        onQuizRetakeFinished={onQuizRetakeFinished}
                        rhymequiz={data?.rhymequizzBySlug as Rhymequizz}
                        settings={playerSettings}
                        isFullScreen={isFullScreen}
                      />

                      {isFullScreen && <ExitFullScreenButton />}
                    </div>
                  </div>
                )}

              {modeState === "retake-summary" && (
                <RetakeSummary
                  coupletState={rhymequizState.coupletState}
                  onSeeReport={onSeeReport}
                />
              )}

              {isShowToggle && (
                <Row
                  justify="space-between"
                  align="middle"
                  css={s.extra.container}
                >
                  <Switch
                    onChange={() => {
                      setShowExtra(!isShowExtra);
                      setShowToggle(true);
                    }}
                    checkedChildren="Hide Quiz Info"
                    unCheckedChildren="Show Quiz Info"
                    size="small"
                  />
                  {(modeState === "player" ||
                    modeState === "retake-full" ||
                    modeState === "retake-wrong") &&
                    width > 768 && (
                      <Button
                        icon={<FullscreenOutlined />}
                        css={{ color: colors.darkGrey }}
                        onClick={onFullscreenClicked}
                      ></Button>
                    )}
                </Row>
              )}

              {isShowExtra && (
                <Row
                  align="middle"
                  justify="space-between"
                  css={s.extra.container}
                >
                  {currentUser?.accountType === UserAccountType.Educator && (
                    // // DESKTOP
                    <div css={{ width: "100%" }}>
                      {width > 768 && (
                        <Row
                          align="top"
                          justify="space-between"
                          css={s.buttonsRoot}
                        >
                          {assignmentsPreview?.nodes.length !== 0 ? (
                            <Button
                              type="text"
                              css={{
                                padding: "4px 5px",
                                [mq.md]: { display: "flex" },
                              }}
                              onClick={() => {
                                track(EVENT_TYPE.QUIZ_ACTION_CLICKED, {
                                  type: "assigned_links",
                                  slug,
                                });
                              }}
                            >
                              <a href={`/rhymequiz/${slug}/links`}>
                                <span>Assigned Links</span>
                              </a>
                              {assignedLinksSvg}
                            </Button>
                          ) : null}
                          <Button
                            type="text"
                            onClick={() => {
                              setVocabModalVisible(true);
                              track(EVENT_TYPE.QUIZ_ACTION_CLICKED, {
                                type: "vocab",
                                slug,
                              });
                            }}
                            css={{
                              padding: "4px 5px",
                              [mq.md]: { display: "flex" },
                            }}
                          >
                            Vocab
                            <FileWordOutlined
                              style={{
                                fontSize: "1.25rem",
                                color: colors.primary,
                              }}
                            />
                          </Button>

                          <Modal
                            visible={isVocabModalVisible}
                            closable={true}
                            maskClosable={true}
                            footer={null}
                            onCancel={() => setVocabModalVisible(false)}
                            title={`Vocabulary Words (${fixedVocab?.length}):`}
                            css={s.modalRoot}
                            style={{ position: "relative", top: "20" }}
                          >
                            {renderedVocabWords}
                          </Modal>

                          <Button
                            type="text"
                            onClick={() => {
                              setLyricsPreviewVisible(true);
                              track(EVENT_TYPE.QUIZ_ACTION_CLICKED, {
                                type: "preview_lyrics",
                                slug,
                              });
                            }}
                            css={{
                              padding: "4px 5px",
                              [mq.md]: { display: "flex" },
                            }}
                          >
                            Preview Lyrics
                            {vocabPreviewSvg}
                          </Button>

                          {isLyricsPreviewVisible && (
                            <Modal
                              css={s.lyricsPreviewModal}
                              maskClosable={false}
                              wrapProps={{
                                style: { background: "rgba(33, 33, 33, 0.8)" },
                              }}
                              visible={isLyricsPreviewVisible}
                              centered
                              footer={null}
                              width={"fit-content"}
                              onCancel={() => setLyricsPreviewVisible(false)}
                            >
                              <LyricsPreview
                                rhymequiz={data?.rhymequizzBySlug as Rhymequizz}
                              />
                            </Modal>
                          )}

                          <LyricsDocButton
                            rhymequiz={data?.rhymequizzBySlug?.id}
                            title="Answer Bank"
                            reportType="answerBankDoc"
                            width={width}
                          />

                          <LyricsDocButton
                            rhymequiz={data?.rhymequizzBySlug?.id}
                            title="Printable Rhymequiz"
                            reportType="printableRhymequizDoc"
                            width={width}
                          />

                          <Button
                            onClick={() =>
                              router.push(`/rhymequiz/${slug}/assign`)
                            }
                            css={{
                              backgroundColor: colors.primary,
                              color: colors.textInverted,
                              padding: "0 35px",
                            }}
                          >
                            Assign
                          </Button>
                        </Row>
                      )}

                      {/* //MOBILE */}
                      {width <= 768 && (
                        <React.Fragment>
                          <Row
                            align="top"
                            justify="space-between"
                            gutter={1}
                            css={s.buttonsMobileRoot}
                          >
                            {assignmentsPreview?.nodes.length !== 0 ? (
                              <Col span={4}>
                                <Row justify="center">
                                  <Button
                                    type="text"
                                    css={{
                                      padding: "4px 10px",
                                      whiteSpace: "break-spaces",
                                      height: "auto",
                                    }}
                                  >
                                    <a href={`/rhymequiz/${slug}/links`}>
                                      {assignedLinksSvg}
                                      Assign Links
                                    </a>
                                  </Button>
                                </Row>
                              </Col>
                            ) : null}
                            <Col span={4}>
                              <Row justify="center">
                                <Button
                                  type="text"
                                  onClick={() => setVocabModalVisible(true)}
                                  css={{
                                    padding: "4px 5px",
                                    whiteSpace: "break-spaces",
                                    height: "auto",
                                    [mq.md]: {
                                      display: "flex",
                                    },
                                  }}
                                >
                                  {vocabPreviewSvg}
                                  Preview Vocab
                                </Button>
                              </Row>
                            </Col>

                            <Modal
                              visible={isVocabModalVisible}
                              closable={true}
                              maskClosable={true}
                              footer={null}
                              onCancel={() => setVocabModalVisible(false)}
                              title={`Vocabulary Words (${fixedVocab?.length}):`}
                              css={s.modalRoot}
                              style={{ position: "relative", top: "20" }}
                            >
                              {renderedVocabWords}
                            </Modal>

                            <LyricsDocButton
                              rhymequiz={data?.rhymequizzBySlug?.id}
                              title="Lyrics"
                              reportType="lyricsDoc"
                              width={width}
                            />

                            <LyricsDocButton
                              rhymequiz={data?.rhymequizzBySlug?.id}
                              title="Answer Bank"
                              reportType="answerBankDoc"
                              width={width}
                            />

                            <LyricsDocButton
                              rhymequiz={data?.rhymequizzBySlug?.id}
                              title="Printable Rhymequiz"
                              reportType="printableRhymequizDoc"
                              width={width}
                            />
                          </Row>
                          <Row>
                            <Col span={24}>
                              <Row justify="end">
                                <Button
                                  onClick={() =>
                                    router.push(`/rhymequiz/${slug}/assign`)
                                  }
                                  css={{
                                    backgroundColor: colors.primary,
                                    color: colors.textInverted,
                                    padding: "0 35px",
                                  }}
                                >
                                  Assign
                                </Button>
                              </Row>
                            </Col>
                          </Row>
                        </React.Fragment>
                      )}
                      <Row justify="end">
                        <AdminSheetButton
                          currentUser={currentUser}
                          rhymequiz={data?.rhymequizzBySlug?.id}
                          css={s.backHomepage}
                        />
                      </Row>
                    </div>
                  )}

                  <Row css={{ marginBottom: 85 }}>
                    <span css={s.recs.title}>
                      More {subjectName} Rhymequizzes
                    </span>

                    <RhymequizRecommendations
                      numRecs={3}
                      subjectId={subjectId}
                      accountType={currentUser?.accountType}
                      currentRhymequiz={data?.rhymequizzBySlug}
                    ></RhymequizRecommendations>
                  </Row>
                </Row>
              )}
            </div>
          ) : (
            <Row justify={"center"}>
              <SimpleGradingReport
                rhymequiz={data?.rhymequizzBySlug as Rhymequizz}
                rzState={rhymequizState}
                retakeRzState={retakeRhymequizState}
              />
            </Row>
          );
        }}
      </SharedLayout>
    </div>
  );
};

export default RhymequizPage;

const assignedLinksSvg = (
  <svg
    width="1.5em"
    height="1.5em"
    viewBox="0 0 32 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M20.7559 18.0042L26.1572 12.603C26.5533 12.2069 26.5533 11.5587 26.1572 11.1626C25.7611 10.7666 25.113 10.7666 24.7169 11.1626L19.3156 16.5639C18.1273 17.7522 16.1829 17.7522 14.9946 16.5639C13.8064 15.3756 13.8064 13.4312 14.9946 12.2429C15.3907 11.8468 15.3907 11.1987 14.9946 10.8026C14.5985 10.4065 13.9504 10.4065 13.5543 10.8026C11.5666 12.7902 11.5666 16.0166 13.5543 18.0042C15.5419 19.9919 18.7683 19.9919 20.7559 18.0042Z"
      fill="#F66701"
    />
    <path
      d="M20.756 6.48198L22.1963 5.04165C23.3846 3.85338 25.3291 3.85338 26.5173 5.04165C27.7056 6.22993 27.7056 8.17437 26.5173 9.36264L24.3568 11.5231C23.9608 11.9192 23.9608 12.5674 24.3568 12.9635C24.7529 13.3596 25.4011 13.3596 25.7972 12.9635L27.9577 10.803C29.9453 8.81532 29.9453 5.58898 27.9577 3.60133C25.97 1.61367 22.7437 1.61367 20.756 3.60132L19.3157 5.04165C18.9196 5.43774 18.9196 6.08589 19.3157 6.48198C19.7118 6.87807 20.3599 6.87807 20.756 6.48198Z"
      fill="#F66701"
    />
    <path
      d="M10.8026 6.99576L5.40138 12.397C5.00529 12.7931 5.00529 13.4413 5.40138 13.8374C5.79747 14.2334 6.44562 14.2334 6.84171 13.8374L12.243 8.43609C13.4312 7.24782 15.3757 7.24782 16.564 8.43609C17.7522 9.62436 17.7522 11.5688 16.564 12.7571C16.1679 13.1532 16.1679 13.8013 16.564 14.1974C16.96 14.5935 17.6082 14.5935 18.0043 14.1974C19.9919 12.2098 19.9919 8.98342 18.0043 6.99576C16.0166 5.00811 12.7903 5.00811 10.8026 6.99576Z"
      fill="#F66701"
    />
    <path
      d="M10.8025 18.518L9.36221 19.9583C8.17394 21.1466 6.2295 21.1466 5.04123 19.9583C3.85296 18.7701 3.85296 16.8256 5.04123 15.6374L7.20172 13.4769C7.59781 13.0808 7.59781 12.4326 7.20172 12.0365C6.80563 11.6404 6.15748 11.6404 5.76139 12.0365L3.6009 14.197C1.61324 16.1847 1.61324 19.411 3.6009 21.3987C5.58855 23.3863 8.81489 23.3863 10.8025 21.3987L12.2429 19.9583C12.639 19.5623 12.639 18.9141 12.2429 18.518C11.8468 18.1219 11.1986 18.1219 10.8025 18.518Z"
      fill="#F66701"
    />
  </svg>
);

const vocabPreviewSvg = (
  <svg
    width="1.5em"
    height="1.5em"
    viewBox="0 0 24 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 0C6.54545 0 1.88727 3.31733 0 8C1.88727 12.6827 6.54545 16 12 16C17.4545 16 22.1127 12.6827 24 8C22.1127 3.31733 17.4545 0 12 0Z"
      fill="#ED4A7C"
    />
    <path
      d="M12 3C9.23333 3 7 5.23333 7 8C7 10.7667 9.23333 13 12 13C14.7667 13 17 10.7667 17 8C17 5.23333 14.7667 3 12 3Z"
      fill="white"
    />
    <path
      d="M12 5C10.34 5 9 6.34 9 8C9 9.66 10.34 11 12 11C13.66 11 15 9.66 15 8C15 6.34 13.66 5 12 5Z"
      fill="#ED4A7C"
    />
  </svg>
);

const s = {
  backHomepage: css({
    margin: "10px 25px",
    textDecoration: "underline",
    [mq.md]: {
      margin: "10px 70px",
    },
  }),
  fullScreenWrapper: (playerScale: number) =>
    css({
      margin: "auto auto",
      maxWidth: "980px",
      transform: `scale(${playerScale})`,
      transformOrigin: "top",
    }),
  recs: {
    title: css({
      marginTop: 25,
      fontSize: 18,
      color: colors.secondary,
      fontWeight: 400,
      [mq.sm]: {
        fontSize: 24,
      },
    }),
  },
  extra: {
    container: css({
      zIndex: 1000,
      margin: "10px 25px",
      [mq.sm]: {
        margin: "10px 70px",
      },
    }),
  },
  word: css({
    paddingTop: 10,
    color: colors.darkGrey,
  }),
  lyricsPreviewModal: css({
    ".ant-modal-wrap": {
      background: "rgba(33, 33, 33, 0.8)",
    },
  }),
  modalRoot: css({
    ".ant-modal-content": css({
      borderRadius: "10px",
      border: "1px solid #FFFFFF",
      paddingBottom: "0px",
      boxShadow: "1px 1px 20px 5px rgba(0, 0, 0, 0.25)",
    }),
    ".ant-modal-body": css({
      fontFamily: "Roboto",
      fontStyle: "normal",
      fontWeight: "normal",
      color: "#FF6B00",
      lineHeight: 1,
      padding: "12px 24px 0px 24px",
    }),
    ".ant-modal-header": css({
      borderRadius: "10px",
      borderBottom: "none",
      padding: "16px 24px 0px 24px",
    }),
    ".ant-modal-title": css({
      color: colors.primary,
    }),
  }),
  buttonsRoot: css({
    width: "100%",
    svg: css({
      marginLeft: ".5em",
    }),
    ".ant-btn-text:hover, .ant-btn-text:focus": css({
      color: "#2d5199",
      borderColor: "transparent",
    }),
  }),
  buttonsMobileRoot: css({
    width: "100%",
    marginBottom: 50,
    svg: css({
      width: "3em",
      height: "3em",
    }),
    fontSize: "10px",
    ".ant-btn-text:hover, .ant-btn-text:focus": css({
      color: "#2d5199",
      borderColor: "transparent",
    }),
  }),
};
